import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  DollarSign, FileText, Clock, CheckCircle, XCircle, AlertTriangle,
  TrendingUp, TrendingDown, RefreshCw, Plus, Send, Eye, Edit,
  PieChart, BarChart3, Calendar, Building, Users
} from 'lucide-react';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0D9488', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

const BillingDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [claims, setClaims] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [agingReport, setAgingReport] = useState(null);
  const [payerMix, setPayerMix] = useState([]);
  const [billingCodes, setBillingCodes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showCreateClaim, setShowCreateClaim] = useState(false);
  
  const [newClaim, setNewClaim] = useState({
    patient_id: '',
    patient_name: '',
    service_date: new Date().toISOString().split('T')[0],
    payer_name: 'Medicare',
    total_charges: 0,
    level_of_care: 'routine',
    claim_type: 'hospice'
  });

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDashboard(),
        loadClaims(),
        loadRevenueSummary(),
        loadAgingReport(),
        loadPayerMix(),
        loadBillingCodes()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const loadDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/billing/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  };

  const loadClaims = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/billing/claims?limit=50`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClaims(data.claims || []);
      }
    } catch (error) {
      console.error('Error loading claims:', error);
    }
  };

  const loadRevenueSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/billing/revenue/summary?period=month`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRevenueSummary(data);
      }
    } catch (error) {
      console.error('Error loading revenue summary:', error);
    }
  };

  const loadAgingReport = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/billing/revenue/aging`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAgingReport(data);
      }
    } catch (error) {
      console.error('Error loading aging report:', error);
    }
  };

  const loadPayerMix = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/billing/revenue/payer-mix`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPayerMix(data.payers || []);
      }
    } catch (error) {
      console.error('Error loading payer mix:', error);
    }
  };

  const loadBillingCodes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/billing/codes`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBillingCodes(data);
      }
    } catch (error) {
      console.error('Error loading billing codes:', error);
    }
  };

  const createClaim = async () => {
    if (!newClaim.patient_id || !newClaim.service_date) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/phase30/billing/claims`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(newClaim)
      });

      if (response.ok) {
        toast.success('Claim created successfully');
        setShowCreateClaim(false);
        loadClaims();
        loadDashboard();
      }
    } catch (error) {
      toast.error('Failed to create claim');
    }
  };

  const submitClaim = async (claimId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/billing/claims/${claimId}/submit?user_id=demo_user`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        toast.success('Claim submitted');
        loadClaims();
        loadDashboard();
      }
    } catch (error) {
      toast.error('Failed to submit claim');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800',
      appealed: 'bg-purple-100 text-purple-800'
    };
    return <Badge className={styles[status] || styles.draft}>{status.toUpperCase()}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="billing-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Prepare chart data
  const agingChartData = agingReport ? Object.entries(agingReport.buckets).map(([key, value]) => ({
    name: key,
    amount: value.amount,
    count: value.count
  })) : [];

  return (
    <div className="space-y-6" data-testid="billing-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="h-7 w-7 text-teal-600" />
            Billing & Revenue
          </h1>
          <p className="text-gray-500">Claims management and revenue cycle</p>
        </div>
        <Dialog open={showCreateClaim} onOpenChange={setShowCreateClaim}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="create-claim-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Claim
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Claim</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Patient ID *</Label>
                <Input
                  value={newClaim.patient_id}
                  onChange={(e) => setNewClaim({...newClaim, patient_id: e.target.value})}
                />
              </div>
              <div>
                <Label>Patient Name</Label>
                <Input
                  value={newClaim.patient_name}
                  onChange={(e) => setNewClaim({...newClaim, patient_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Service Date *</Label>
                <Input
                  type="date"
                  value={newClaim.service_date}
                  onChange={(e) => setNewClaim({...newClaim, service_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Payer</Label>
                <Select value={newClaim.payer_name} onValueChange={(v) => setNewClaim({...newClaim, payer_name: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Medicare">Medicare</SelectItem>
                    <SelectItem value="Medicaid">Medicaid</SelectItem>
                    <SelectItem value="Private Insurance">Private Insurance</SelectItem>
                    <SelectItem value="Self Pay">Self Pay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Level of Care</Label>
                <Select value={newClaim.level_of_care} onValueChange={(v) => setNewClaim({...newClaim, level_of_care: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="routine">Routine Home Care</SelectItem>
                    <SelectItem value="continuous">Continuous Home Care</SelectItem>
                    <SelectItem value="respite">Inpatient Respite</SelectItem>
                    <SelectItem value="inpatient">General Inpatient</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Total Charges</Label>
                <Input
                  type="number"
                  value={newClaim.total_charges}
                  onChange={(e) => setNewClaim({...newClaim, total_charges: parseFloat(e.target.value) || 0})}
                />
              </div>
              <Button className="w-full" onClick={createClaim}>Create Claim</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">MTD Revenue</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(dashboard.mtd_revenue)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Claims</p>
                  <p className="text-2xl font-bold">{dashboard.pending_claims}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Days in A/R</p>
                  <p className="text-2xl font-bold">{dashboard.days_in_ar}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Clean Claim Rate</p>
                  <p className="text-2xl font-bold">{dashboard.clean_claim_rate}%</p>
                </div>
                <CheckCircle className="h-8 w-8 text-teal-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="claims">Claims</TabsTrigger>
          <TabsTrigger value="codes">Billing Codes</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* A/R Aging */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">A/R Aging</CardTitle>
                <CardDescription>Total: {formatCurrency(agingReport?.total_ar)}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={agingChartData}>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="amount" fill="#0D9488" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Revenue Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Revenue Summary (MTD)</CardTitle>
              </CardHeader>
              <CardContent>
                {revenueSummary && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span>Total Charges</span>
                      <span className="font-bold">{formatCurrency(revenueSummary.total_charges)}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                      <span>Collected</span>
                      <span className="font-bold text-green-600">{formatCurrency(revenueSummary.total_collected)}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                      <span>Pending</span>
                      <span className="font-bold text-yellow-600">{formatCurrency(revenueSummary.total_pending)}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                      <span>Denied</span>
                      <span className="font-bold text-red-600">{formatCurrency(revenueSummary.total_denied)}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-teal-50 rounded">
                      <span>Collection Rate</span>
                      <span className="font-bold text-teal-600">{revenueSummary.collection_rate}%</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="claims" className="mt-4">
          {claims.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No claims found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {claims.map((claim) => (
                <Card key={claim.id}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-medium">{claim.id}</span>
                          {getStatusBadge(claim.status)}
                          <Badge variant="outline" className="capitalize">{claim.level_of_care}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {claim.patient_name || claim.patient_id} • {claim.payer_name}
                        </p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                          <span>Service: {formatDate(claim.service_date)}</span>
                          <span>Charges: {formatCurrency(claim.total_charges)}</span>
                          {claim.payment_amount && <span className="text-green-600">Paid: {formatCurrency(claim.payment_amount)}</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {claim.status === 'draft' && (
                          <Button size="sm" onClick={() => submitClaim(claim.id)}>
                            <Send className="h-4 w-4 mr-1" />
                            Submit
                          </Button>
                        )}
                        <Button size="sm" variant="outline">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="codes" className="mt-4">
          {billingCodes && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Level of Care Rates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {billingCodes.level_of_care_rates?.map((rate) => (
                      <div key={rate.code} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <p className="font-medium">{rate.description}</p>
                          <p className="text-xs text-gray-500">{rate.code}</p>
                        </div>
                        <span className="font-bold text-teal-600">
                          {rate.daily_rate ? `$${rate.daily_rate}/day` : `$${rate.hourly_rate}/hr`}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Revenue Codes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {billingCodes.revenue_codes?.map((code) => (
                      <div key={code.code} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span className="font-mono">{code.code}</span>
                        <span className="text-sm">{code.description}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingDashboard;
